/**
 * formulaEngine.js
 *
 * Safe expression evaluator for the Data Explorer formula builder.
 * Supports:
 *   • Free-form math expressions  (row-level)
 *   • Aggregate functions          SUM / AVG / MIN / MAX → scalar across all rows
 *   • Math helpers                 ABS / ROUND / SQRT / POW
 *   • Preset formulas              rolling_avg / delta / ratio
 */

import { create, all } from 'mathjs';

// Create a minimal mathjs instance
const math = create(all, { number: 'number' });

/* ──────────────────────────── helpers ──────────────────────────── */

const AGG_REGEX = /\b(SUM|AVG|MIN|MAX)\s*\(\s*([a-zA-Z_]\w*)\s*\)/gi;

/**
 * Pre-compute aggregate values referenced in the expression.
 * Replaces  SUM(field)  with the numeric literal so the per-row
 * evaluation only has to deal with simple math.
 */
function resolveAggregates(expression, allData) {
  return expression.replace(AGG_REGEX, (_match, fn, field) => {
    const vals = allData
      .map(r => Number(r[field]))
      .filter(v => !isNaN(v));
    if (vals.length === 0) return '0';
    switch (fn.toUpperCase()) {
      case 'SUM': return String(vals.reduce((a, b) => a + b, 0));
      case 'AVG': return String(vals.reduce((a, b) => a + b, 0) / vals.length);
      case 'MIN': return String(Math.min(...vals));
      case 'MAX': return String(Math.max(...vals));
      default:    return '0';
    }
  });
}

/* ────────────────── expression evaluation (per row) ────────────── */

/**
 * Evaluate a free-form formula string for a single row.
 *
 * @param {string}   expression  e.g. "(net_revenue_post_dsm / gross_revenue) * 100"
 * @param {Object}   row         current data row  { gross_revenue: 123, ... }
 * @param {Object[]} allData     the full dataset (needed for SUM/AVG/… )
 * @returns {number|null}
 */
export function evaluateExpression(expression, row, allData) {
  try {
    // 1. Resolve aggregate calls first
    let resolved = resolveAggregates(expression, allData);

    // 2. Build scope from row values (skip non-numeric / meta keys)
    const scope = {};
    for (const [key, value] of Object.entries(row)) {
      if (key === 'period') continue;
      const n = Number(value);
      scope[key] = isNaN(n) ? 0 : n;
    }

    // 3. Evaluate with mathjs (safe – no eval())
    const result = math.evaluate(resolved, scope);
    if (typeof result !== 'number' || !isFinite(result)) return null;
    return result;
  } catch {
    return null;
  }
}

/* ────────────────────── preset formulas ────────────────────── */

/**
 * Apply a preset formula to the entire dataset and return the
 * augmented rows (mutates in place for performance).
 */
export function applyPresetFormula(data, formula) {
  const { name, type, baseCol, param } = formula;
  const windowSize = formula.windowSize || 7;

  if (type === 'rolling_avg') {
    for (let i = 0; i < data.length; i++) {
      let sum = 0;
      let count = 0;
      for (let j = Math.max(0, i - windowSize + 1); j <= i; j++) {
        sum += Number(data[j][baseCol]) || 0;
        count++;
      }
      data[i][name] = count > 0 ? sum / count : 0;
    }
  } else if (type === 'delta') {
    for (let i = 0; i < data.length; i++) {
      if (i === 0) {
        data[i][name] = 0;
      } else {
        const cur = Number(data[i][baseCol]) || 0;
        const prev = Number(data[i - 1][baseCol]) || 0;
        data[i][name] = cur - prev;
      }
    }
  } else if (type === 'ratio') {
    for (let i = 0; i < data.length; i++) {
      const num = Number(data[i][baseCol]) || 0;
      const den = Number(data[i][param]) || 0;
      data[i][name] = den === 0 ? null : num / den;
    }
  }

  return data;
}

/* ──────────────── apply ALL formulas to a dataset ──────────────── */

/**
 * Given raw data and a list of formula definitions, return a new array
 * with computed formula columns attached.
 *
 * Each formula has shape:
 *   { id, name, type: 'expression'|'rolling_avg'|'delta'|'ratio',
 *     expression?, baseCol?, param?, windowSize? }
 */
export function applyFormulas(rawData, formulas) {
  if (!rawData.length || !formulas.length) return rawData;
  // Deep-clone so we don't mutate the original
  const data = rawData.map(r => ({ ...r }));

  for (const f of formulas) {
    if (f.type === 'expression') {
      for (let i = 0; i < data.length; i++) {
        data[i][f.name] = evaluateExpression(f.expression, data[i], data);
      }
    } else {
      applyPresetFormula(data, f);
    }
  }
  return data;
}

/* ──────────────────── validation ──────────────────── */

/**
 * Validate a formula expression before saving.
 *
 * @param {string}   expression
 * @param {string[]} availableFields  list of valid field identifiers
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateFormula(expression, availableFields) {
  if (!expression || !expression.trim()) {
    return { valid: false, error: 'Formula cannot be empty.' };
  }

  // Strip aggregate calls for validation (field inside them is checked separately)
  let stripped = expression.replace(AGG_REGEX, (_m, _fn, field) => {
    if (!availableFields.includes(field)) {
      return `__UNKNOWN_FIELD_${field}__`;
    }
    return '1'; // replace with a number placeholder
  });

  // Check for unknown fields injected by the replace
  const unknownMatch = stripped.match(/__UNKNOWN_FIELD_(\w+)__/);
  if (unknownMatch) {
    return { valid: false, error: `Unknown field: "${unknownMatch[1]}"` };
  }

  // Build a test scope with all fields = 1
  const scope = {};
  availableFields.forEach(f => { scope[f] = 1; });

  try {
    math.evaluate(stripped, scope);
    return { valid: true };
  } catch (err) {
    return { valid: false, error: err.message };
  }
}
